import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdVisibility, MdPrint } from 'react-icons/md';
import '../master/MasterData.css';

interface Customer {
  customerId: number;
  customerCode: string;
  customerName: string;
  customerType: string;
  tin: string;
  email: string;
  phone: string;
  city: string;
  isActive: string;
}

interface QuotationItem {
  itemId: number;
  itemCode: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

interface SalesQuotation {
  quotationId: number;
  quotationNo: string;
  quotationDate: string;
  customerCode: string;
  customerName: string;
  validUntil: string;
  totalAmount: number;
  status: 'DRAFT' | 'SENT' | 'APPROVED' | 'REJECTED' | 'EXPIRED';
  items: QuotationItem[];
  remarks: string;
}

const SalesQuotations: React.FC = () => {
  const [quotations, setQuotations] = useState<SalesQuotation[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState<SalesQuotation | null>(null);
  const [viewMode, setViewMode] = useState(false);

  useEffect(() => {
    // TODO: Fetch customers from API
    // Mock data for now
    setCustomers([
      {
        customerId: 1,
        customerCode: 'CUST001',
        customerName: 'SM Furniture Store',
        customerType: 'CORPORATE',
        tin: '123-456-789-000',
        email: 'sm@furniture.ph',
        phone: '+63 2 1234 5678',
        city: 'Manila',
        isActive: 'Y'
      },
      {
        customerId: 2,
        customerCode: 'CUST002',
        customerName: 'Home Depot Philippines',
        customerType: 'CORPORATE',
        tin: '987-654-321-000',
        email: 'homedepot@example.ph',
        phone: '+63 2 9876 5432',
        city: 'Quezon City',
        isActive: 'Y'
      }
    ]);

    // TODO: Fetch quotations from API
    // Mock data for now
    setQuotations([
      {
        quotationId: 1,
        quotationNo: 'QT-2025-0001',
        quotationDate: '2025-01-15',
        customerCode: 'CUST001',
        customerName: 'SM Furniture Store',
        validUntil: '2025-02-15',
        totalAmount: 450000,
        status: 'SENT',
        items: [
          {
            itemId: 1,
            itemCode: 'SOFA-001',
            itemName: 'Modern Sofa Set',
            quantity: 2,
            unitPrice: 125000,
            amount: 250000
          },
          {
            itemId: 2,
            itemCode: 'TABLE-001',
            itemName: 'Dining Table',
            quantity: 1,
            unitPrice: 200000,
            amount: 200000
          }
        ],
        remarks: 'Special discount applied'
      },
      {
        quotationId: 2,
        quotationNo: 'QT-2025-0002',
        quotationDate: '2025-01-20',
        customerCode: 'CUST002',
        customerName: 'Home Depot Philippines',
        validUntil: '2025-02-20',
        totalAmount: 850000,
        status: 'APPROVED',
        items: [],
        remarks: ''
      }
    ]);
  }, []);

  const filteredQuotations = quotations.filter(quotation =>
    quotation.quotationNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    quotation.customerName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingQuotation(null);
    setViewMode(false);
    setShowModal(true);
  };

  const handleEdit = (quotation: SalesQuotation) => {
    setEditingQuotation(quotation);
    setViewMode(false);
    setShowModal(true);
  };

  const handleView = (quotation: SalesQuotation) => {
    setEditingQuotation(quotation);
    setViewMode(true);
    setShowModal(true);
  };

  const handleDelete = (quotationId: number) => {
    if (window.confirm('Are you sure you want to delete this quotation?')) {
      setQuotations(quotations.filter(q => q.quotationId !== quotationId));
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'APPROVED': return 'badge-success';
      case 'SENT': return 'badge-info';
      case 'REJECTED': return 'badge-danger';
      case 'EXPIRED': return 'badge-warning';
      default: return 'badge-secondary';
    }
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Sales Quotations</h2>
            <p className="subtitle">Manage customer quotations and pricing</p>
          </div>
          <button className="btn btn-primary" onClick={handleAdd}>
            <MdAdd /> Create New Quotation
          </button>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search quotations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Quotation No</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Valid Until</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredQuotations.map(quotation => (
                  <tr key={quotation.quotationId}>
                    <td className="code">{quotation.quotationNo}</td>
                    <td>{new Date(quotation.quotationDate).toLocaleDateString('en-US')}</td>
                    <td>{quotation.customerName}</td>
                    <td>{new Date(quotation.validUntil).toLocaleDateString('en-US')}</td>
                    <td className="amount">₱{quotation.totalAmount.toLocaleString()}</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(quotation.status)}`}>
                        {quotation.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-view"
                          onClick={() => handleView(quotation)}
                          title="View"
                        >
                          <MdVisibility />
                        </button>
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEdit(quotation)}
                          title="Edit"
                        >
                          <MdEdit />
                        </button>
                        <button
                          className="btn-icon btn-print"
                          onClick={() => window.print()}
                          title="Print"
                        >
                          <MdPrint />
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => handleDelete(quotation.quotationId)}
                          title="Delete"
                        >
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredQuotations.length === 0 && (
              <div className="no-data">
                <p>No quotations found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <QuotationModal
          quotation={editingQuotation}
          viewMode={viewMode}
          customers={customers}
          onClose={() => setShowModal(false)}
          onSave={(quotation) => {
            if (editingQuotation) {
              setQuotations(quotations.map(q =>
                q.quotationId === quotation.quotationId ? quotation : q
              ));
            } else {
              setQuotations([...quotations, { ...quotation, quotationId: Date.now() }]);
            }
            setShowModal(false);
          }}
        />
      )}
    </MainLayout>
  );
};

interface QuotationModalProps {
  quotation: SalesQuotation | null;
  viewMode: boolean;
  customers: Customer[];
  onClose: () => void;
  onSave: (quotation: SalesQuotation) => void;
}

const QuotationModal: React.FC<QuotationModalProps> = ({ quotation, viewMode, customers, onClose, onSave }) => {
  const [formData, setFormData] = useState<SalesQuotation>(
    quotation || {
      quotationId: 0,
      quotationNo: '',
      quotationDate: new Date().toISOString().split('T')[0],
      customerCode: '',
      customerName: '',
      validUntil: '',
      totalAmount: 0,
      status: 'DRAFT',
      items: [],
      remarks: ''
    }
  );

  const handleCustomerChange = (customerCode: string) => {
    const selectedCustomer = customers.find(c => c.customerCode === customerCode);
    if (selectedCustomer) {
      setFormData({
        ...formData,
        customerCode: selectedCustomer.customerCode,
        customerName: selectedCustomer.customerName
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!viewMode) {
      onSave(formData);
    }
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        {
          itemId: Date.now(),
          itemCode: '',
          itemName: '',
          quantity: 1,
          unitPrice: 0,
          amount: 0
        }
      ]
    });
  };

  const handleRemoveItem = (itemId: number) => {
    setFormData({
      ...formData,
      items: formData.items.filter(item => item.itemId !== itemId)
    });
  };

  const handleItemChange = (itemId: number, field: keyof QuotationItem, value: any) => {
    setFormData({
      ...formData,
      items: formData.items.map(item => {
        if (item.itemId === itemId) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'quantity' || field === 'unitPrice') {
            updatedItem.amount = updatedItem.quantity * updatedItem.unitPrice;
          }
          return updatedItem;
        }
        return item;
      })
    });
  };

  useEffect(() => {
    const total = formData.items.reduce((sum, item) => sum + item.amount, 0);
    setFormData(prev => ({ ...prev, totalAmount: total }));
  }, [formData.items]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{viewMode ? 'View Quotation' : quotation ? 'Edit Quotation' : 'Create New Quotation'}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row form-row-3">
              <div className="form-group">
                <label>Quotation No *</label>
                <input
                  type="text"
                  required
                  disabled={viewMode}
                  value={formData.quotationNo}
                  onChange={(e) => setFormData({ ...formData, quotationNo: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Quotation Date *</label>
                <input
                  type="date"
                  lang="en-US"
                  data-date-format="MM/DD/YYYY"
                  required
                  disabled={viewMode}
                  value={formData.quotationDate}
                  onChange={(e) => setFormData({ ...formData, quotationDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Valid Until *</label>
                <input
                  type="date"
                  lang="en-US"
                  data-date-format="MM/DD/YYYY"
                  required
                  disabled={viewMode}
                  value={formData.validUntil}
                  onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row form-row-3">
              <div className="form-group">
                <label>Customer Name *</label>
                <select
                  required
                  disabled={viewMode}
                  value={formData.customerCode}
                  onChange={(e) => handleCustomerChange(e.target.value)}
                >
                  <option value="">Select Customer</option>
                  {customers.filter(c => c.isActive === 'Y').map(customer => (
                    <option key={customer.customerId} value={customer.customerCode}>
                      {customer.customerName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Customer Code *</label>
                <input
                  type="text"
                  required
                  disabled
                  value={formData.customerCode}
                  readOnly
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  disabled={viewMode}
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                >
                  <option value="DRAFT">Draft</option>
                  <option value="SENT">Sent</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="EXPIRED">Expired</option>
                </select>
              </div>
            </div>

            <div className="form-section">
              <div className="section-header">
                <h4>Items</h4>
                {!viewMode && (
                  <button type="button" className="btn btn-sm btn-primary" onClick={handleAddItem}>
                    <MdAdd /> Add Item
                  </button>
                )}
              </div>

              <table className="item-table">
                <thead>
                  <tr>
                    <th>Item Code</th>
                    <th>Item Name</th>
                    <th>Quantity</th>
                    <th>Unit Price</th>
                    <th>Amount</th>
                    {!viewMode && <th>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map(item => (
                    <tr key={item.itemId}>
                      <td>
                        <input
                          type="text"
                          disabled={viewMode}
                          value={item.itemCode}
                          onChange={(e) => handleItemChange(item.itemId, 'itemCode', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          disabled={viewMode}
                          value={item.itemName}
                          onChange={(e) => handleItemChange(item.itemId, 'itemName', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          disabled={viewMode}
                          value={item.quantity}
                          onChange={(e) => handleItemChange(item.itemId, 'quantity', parseFloat(e.target.value))}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          disabled={viewMode}
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(item.itemId, 'unitPrice', parseFloat(e.target.value))}
                        />
                      </td>
                      <td>₱{item.amount.toLocaleString()}</td>
                      {!viewMode && (
                        <td>
                          <button
                            type="button"
                            className="btn-icon btn-delete"
                            onClick={() => handleRemoveItem(item.itemId)}
                          >
                            <MdDelete />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold' }}>Total:</td>
                    <td style={{ fontWeight: 'bold' }}>₱{formData.totalAmount.toLocaleString()}</td>
                    {!viewMode && <td></td>}
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="form-group">
              <label>Remarks</label>
              <textarea
                disabled={viewMode}
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {viewMode ? 'Close' : 'Cancel'}
            </button>
            {!viewMode && (
              <button type="submit" className="btn btn-primary">
                {quotation ? 'Update' : 'Create'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default SalesQuotations;
